import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs } from 'firebase/firestore';
import { Trash2, Users, Search, Plus } from 'lucide-react';
import { db } from '../../firebase';
import {
  getPartners,
  createPartner,
  updatePartner,
  buildMemberWrite,
} from '../../services/partnersService';
import { getPortalEmoji } from '../../utils/portalEmojis';
import PortalBackground from '../../components/PortalBackground';

const TEAMS = [
  'General',
  'Sales',
  'Operations',
  'Finance',
  'Technology',
  'Marketing',
  'HR',
];

/** Tailwind card shell: background + border (readable text on top) */
const TEAM_CARD_STYLES = {
  General:
    'bg-slate-100 dark:bg-slate-800/90 border-slate-300 dark:border-slate-600 hover:border-slate-400 dark:hover:border-slate-500',
  Sales:
    'bg-rose-50 dark:bg-rose-950/50 border-rose-200 dark:border-rose-800 hover:border-rose-300 dark:hover:border-rose-600',
  Operations:
    'bg-sky-50 dark:bg-sky-950/40 border-sky-200 dark:border-sky-800 hover:border-sky-300 dark:hover:border-sky-600',
  Finance:
    'bg-emerald-50 dark:bg-emerald-950/40 border-emerald-200 dark:border-emerald-800 hover:border-emerald-300 dark:hover:border-emerald-600',
  Technology:
    'bg-violet-50 dark:bg-violet-950/40 border-violet-200 dark:border-violet-800 hover:border-violet-300 dark:hover:border-violet-600',
  Marketing:
    'bg-amber-50 dark:bg-amber-950/40 border-amber-200 dark:border-amber-800 hover:border-amber-300 dark:hover:border-amber-600',
  HR: 'bg-teal-50 dark:bg-teal-950/40 border-teal-200 dark:border-teal-800 hover:border-teal-300 dark:hover:border-teal-600',
};

const emptyForm = () => ({
  companyName: '',
  team: 'General',
  memberRows: [{ userId: '', dateJoined: '', partnerRole: '' }],
});

const Partners = () => {
  const navigate = useNavigate();
  const [partners, setPartners] = useState([]);
  const [userById, setUserById] = useState({});
  const [internalUsersList, setInternalUsersList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [teamFilter, setTeamFilter] = useState('All');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [partnersList, usersSnap] = await Promise.all([
        getPartners(),
        getDocs(collection(db, 'internal_users')),
      ]);
      const map = {};
      const list = [];
      usersSnap.forEach((d) => {
        const row = { id: d.id, ...d.data() };
        map[d.id] = row;
        list.push(row);
      });
      list.sort((a, b) =>
        `${a.firstName || ''} ${a.lastName || ''}`.localeCompare(
          `${b.firstName || ''} ${b.lastName || ''}`,
          undefined,
          { sensitivity: 'base' }
        )
      );
      setPartners(partnersList);
      setUserById(map);
      setInternalUsersList(list);
    } catch (e) {
      console.error(e);
      setError('Failed to load partners.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const displayName = (uid) => {
    const u = userById[uid];
    if (!u) return 'Unknown user';
    const n = `${u.firstName || ''} ${u.lastName || ''}`.trim();
    return n || u.email || uid;
  };

  /** Resolve an internal user's profile picture across common field names */
  const getUserAvatarUrl = (u) =>
    u?.profileImage ||
    u?.photoURL ||
    u?.photoUrl ||
    u?.profilePictureUrl ||
    u?.profilePicture ||
    u?.profilePic ||
    u?.avatarUrl ||
    u?.avatarURL ||
    u?.imageUrl ||
    u?.imageURL ||
    u?.profile?.photoURL ||
    u?.profile?.profileImage ||
    '';

  const userInitials = (u) => {
    const i = `${u?.firstName?.[0] || ''}${u?.lastName?.[0] || ''}`.trim();
    return (i || u?.email?.[0] || '?').toUpperCase();
  };

  /** One profile-picture avatar, same footprint as the logo (w-14 h-14) */
  const renderMemberAvatar = (m, key) => {
    const u = userById[m.userId];
    const url = getUserAvatarUrl(u);
    return (
      <div
        key={key}
        className="w-14 h-14 shrink-0 rounded-full overflow-hidden bg-gray-100 dark:bg-gray-600 ring-1 ring-gray-200 dark:ring-gray-500 flex items-center justify-center"
        title={displayName(m.userId)}
      >
        {url ? (
          <>
            <img
              src={url}
              alt=""
              className="w-14 h-14 object-cover"
              onError={(e) => {
                e.target.style.display = 'none';
                const next = e.target.nextSibling;
                if (next) next.style.display = 'flex';
              }}
            />
            <div className="w-14 h-14 hidden items-center justify-center rounded-full bg-gray-400 font-semibold text-white text-base">
              {userInitials(u)}
            </div>
          </>
        ) : (
          <div className="w-14 h-14 flex items-center justify-center rounded-full bg-gray-400 font-semibold text-white text-base">
            {userInitials(u)}
          </div>
        )}
      </div>
    );
  };

  /** Split members so the logo sits in the middle; a single member lands on the right */
  const splitAroundLogo = (members) => {
    const arr = members || [];
    const leftCount = Math.floor(arr.length / 2);
    return { left: arr.slice(0, leftCount), right: arr.slice(leftCount) };
  };

  /** First linked internal user = main contact for the card */
  const mainContactName = (partner) => {
    const first = partner.members?.[0]?.userId;
    if (!first) return 'No main contact';
    return displayName(first);
  };

  const cardStyle = (team) => TEAM_CARD_STYLES[team] || TEAM_CARD_STYLES.General;

  const query = search.trim().toLowerCase();
  const filteredPartners = partners.filter((p) => {
    if (teamFilter !== 'All' && (p.team || 'General') !== teamFilter) return false;
    if (!query) return true;
    const haystack = `${p.companyName || ''} ${mainContactName(p)}`.toLowerCase();
    return haystack.includes(query);
  });

  // Group cards under department headers (TEAMS order), skipping empty departments.
  const groupedPartners = TEAMS.map((team) => ({
    team,
    items: filteredPartners.filter((p) => (p.team || 'General') === team),
  })).filter((g) => g.items.length > 0);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm());
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingId(null);
    setForm(emptyForm());
  };

  const setMemberRow = (index, field, value) => {
    setForm((prev) => {
      const memberRows = [...prev.memberRows];
      memberRows[index] = { ...memberRows[index], [field]: value };
      return { ...prev, memberRows };
    });
  };

  const addMemberRow = () => {
    setForm((prev) => ({
      ...prev,
      memberRows: [...prev.memberRows, { userId: '', dateJoined: '', partnerRole: '' }],
    }));
  };

  const removeMemberRow = (index) => {
    setForm((prev) => ({
      ...prev,
      memberRows: prev.memberRows.filter((_, i) => i !== index),
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const members = form.memberRows
        .map((row) =>
          buildMemberWrite({
            userId: row.userId,
            dateJoinedInput: row.dateJoined,
            partnerRole: row.partnerRole,
          })
        )
        .filter(Boolean);
      if (editingId) {
        await updatePartner(editingId, {
          companyName: form.companyName.trim(),
          team: form.team,
          members,
        });
      } else {
        await createPartner({
          companyName: form.companyName.trim(),
          team: form.team,
          members,
        });
      }
      closeModal();
      await load();
    } catch (err) {
      console.error(err);
      setError(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen p-4 relative">
      <PortalBackground />
      <div className="relative">
      <div className="mb-6">
        <h1 className="text-3xl font-bold tracking-tight text-center text-gray-900 dark:text-white pt-1">Partners</h1>
        <div className="mt-4 flex items-center gap-3">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search partners…"
              className="w-full pl-10 pr-3 py-3 border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 shadow-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500"
            />
          </div>
          <select
            value={teamFilter}
            onChange={(e) => setTeamFilter(e.target.value)}
            className="sm:w-56 py-3 px-3 border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 shadow-sm text-gray-900 dark:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500"
          >
            <option value="All">All departments</option>
            {TEAMS.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={openCreate}
            className="inline-flex items-center justify-center gap-1.5 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium whitespace-nowrap"
          >
            <Plus className="w-4 h-4" />
            Add New
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 rounded border border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-950 text-red-800 dark:text-red-200">
          {error}
        </div>
      )}

      {loading ? (
        <p className="text-gray-500 dark:text-gray-400 text-center">Loading…</p>
      ) : partners.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-gray-300 dark:border-gray-700 rounded-2xl bg-white/70 dark:bg-gray-900/50">
          <Users className="w-12 h-12 mx-auto text-gray-400 mb-3" />
          <p className="text-gray-600 dark:text-gray-300 mb-4">No partners yet.</p>
          <button
            type="button"
            onClick={openCreate}
            className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium"
          >
            Add your first partner
          </button>
        </div>
      ) : filteredPartners.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-gray-300 dark:border-gray-700 rounded-2xl bg-white/70 dark:bg-gray-900/50">
          <Users className="w-12 h-12 mx-auto text-gray-400 mb-3" />
          <p className="text-gray-600 dark:text-gray-300">No partners match your search.</p>
        </div>
      ) : (
        groupedPartners.map((group) => (
          <div key={group.team} className="mt-10 first:mt-0">
            {/* Department header — centered divider + emoji tile + label */}
            <div className="flex items-center justify-center gap-3 mb-5">
              <span className="h-px flex-1 max-w-[110px] bg-gray-200 dark:bg-gray-700" />
              <span className="shrink-0 w-10 h-10 rounded-xl bg-red-50 dark:bg-red-900/30 flex items-center justify-center">
                <span className="text-xl leading-none">{getPortalEmoji(group.team)}</span>
              </span>
              <h2 className="text-xl md:text-2xl font-bold tracking-tight leading-tight text-center text-gray-900 dark:text-white">
                {group.team}
              </h2>
              <span className="h-px flex-1 max-w-[110px] bg-gray-200 dark:bg-gray-700" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {group.items.map((p) => (
                <div
                  key={p.id}
                  className={`relative rounded-2xl border-2 shadow-sm transition-all hover:shadow-md hover:-translate-y-0.5 ${cardStyle(p.team)}`}
                >
                  <button
                    type="button"
                    onClick={() => navigate(`/crm/partners/${p.id}`)}
                    className="w-full text-center px-5 pt-8 pb-5 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500 rounded-xl"
                  >
                    <div className="text-lg font-semibold text-gray-900 dark:text-white leading-snug">
                      {p.companyName || 'Unnamed partner'}
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-300 mt-1 text-center font-medium">
                      {mainContactName(p)}
                    </div>

                    {/* Team profile pictures + company logo — one row, logo centered */}
                    {(p.logoUrl || p.members?.length > 0) ? (
                      (() => {
                        const { left, right } = splitAroundLogo(p.members);
                        return (
                          <div className="mt-4 flex flex-row flex-wrap items-center justify-center gap-2">
                            {left.map((m, idx) => renderMemberAvatar(m, m.userId || `l-${idx}`))}
                            {p.logoUrl ? (
                              <div className="w-14 h-14 shrink-0 rounded-xl overflow-hidden bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 flex items-center justify-center">
                                <img
                                  src={p.logoUrl}
                                  alt=""
                                  className="max-w-[85%] max-h-[85%] object-contain"
                                />
                              </div>
                            ) : null}
                            {right.map((m, idx) => renderMemberAvatar(m, m.userId || `r-${idx}`))}
                          </div>
                        );
                      })()
                    ) : null}
                  </button>
                </div>
              ))}
            </div>
          </div>
        ))
      )}

      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto border border-gray-200 dark:border-gray-800">
            <form onSubmit={handleSave} className="p-6">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                {editingId ? 'Edit partner' : 'Add partner'}
              </h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Company name *
                  </label>
                  <input
                    required
                    value={form.companyName}
                    onChange={(e) => setForm((f) => ({ ...f, companyName: e.target.value }))}
                    className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Team</label>
                  <select
                    value={form.team}
                    onChange={(e) => setForm((f) => ({ ...f, team: e.target.value }))}
                    className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                  >
                    {TEAMS.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Team members</label>
                    <button
                      type="button"
                      onClick={addMemberRow}
                      className="text-sm text-red-600 dark:text-red-400 hover:underline"
                    >
                      + Add member
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                    First member is shown as the main contact on the card. Link people from Internal Users.
                  </p>
                  <div className="space-y-3">
                    {form.memberRows.map((row, i) => (
                      <div
                        key={i}
                        className="flex flex-col gap-2 p-3 border border-gray-200 dark:border-gray-700 rounded bg-gray-50 dark:bg-gray-800/50"
                      >
                        <div className="flex flex-wrap gap-2 items-end">
                          <div className="flex-1 min-w-[140px]">
                            <label className="text-xs text-gray-500 dark:text-gray-400">Internal user</label>
                            <select
                              value={row.userId}
                              onChange={(e) => setMemberRow(i, 'userId', e.target.value)}
                              className="w-full mt-0.5 p-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
                            >
                              <option value="">Select…</option>
                              {internalUsersList.map((u) => (
                                <option key={u.id} value={u.id}>
                                  {`${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email} — {u.email}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div className="w-36">
                            <label className="text-xs text-gray-500 dark:text-gray-400">Date joined</label>
                            <input
                              type="date"
                              value={row.dateJoined}
                              onChange={(e) => setMemberRow(i, 'dateJoined', e.target.value)}
                              className="w-full mt-0.5 p-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
                            />
                          </div>
                          {form.memberRows.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeMemberRow(i)}
                              className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-950 rounded"
                              title="Remove row"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                        <input
                          type="text"
                          placeholder="Partner-specific role (optional)"
                          value={row.partnerRole}
                          onChange={(e) => setMemberRow(i, 'partnerRole', e.target.value)}
                          className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div className="flex justify-end gap-2 mt-6">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded text-gray-700 dark:text-gray-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white rounded font-medium"
                >
                  {saving ? 'Saving…' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default Partners;
